/**
 * 奥羽・越後の国データ
 *
 * 担当: 陸奥(16) / 出羽(17) / 越後(19)
 *
 * 執筆基準は `_reference.ts`（尾張・甲斐）に合わせている。
 * docs/05-学習設計.md §4 の原則を守ること。
 *   - 武将を英雄視・悪役視しない。戦闘行為を美化しない
 *   - 通説と異説がある事項は caveat に書く
 *   - ゲームの都合による改変は gameNote に書く
 *   - 合戦だけでなく検地・交易・治水など生活の事実も入れる
 */

import type { Province } from '../types';

/**
 * 令制国で最大の面積を持つ国。盤上でも石高ランク4。
 * 北上川流域の平野と馬の産地であることから地形は平野（地の利は騎馬）。
 */
export const MUTSU: Province = {
  id: 16,
  kind: 'province',
  name: '陸奥',
  reading: 'むつ',
  region: 'tohoku',
  kokudaka: 4,
  defense: 3,
  terrain: 'plain',
  historicalKokudaka: 167.2,
  note: {
    summary:
      '令制国で最も広い国。北上川流域に平野が開け、馬と砂金の産地として知られた。多くの領主が分立し、豊臣秀吉の奥州仕置で領主が大きく入れ替わった。',
    detail: {
      1: 'いまの福島・宮城・岩手・青森県などにあたる、とても広い国です。川ぞいに平野があり、よい馬がそだつことで知られました。むかしから金がとれた土地でもあります。たくさんの大名がならびたっていて、一つにまとまるのがおそい国でした。',
      2: '現在の福島・宮城・岩手・青森の各県と秋田県の一部にあたる、令制国で最大の面積を持つ国。北上川や阿武隈川の流域に平野が開け、北部の糠部（ぬかのぶ）は名馬の産地として知られた。奈良時代には陸奥から金が朝廷に献上され、東大寺の大仏に使われている。戦国時代には伊達・蘆名・南部・葛西・大崎・相馬など多くの領主が分立し、1589年の摺上原の戦いで伊達政宗が蘆名氏を破った。しかし1590年の豊臣秀吉による奥州仕置で領主の配置が大きく変えられ、それに反発した一揆も起きた。',
      3: '現在の福島・宮城・岩手・青森の四県および秋田県の一部にあたる、令制国で最大の面積を持つ国。北上川・阿武隈川の流域に平野が広がる一方、奥羽山脈と北上高地に挟まれた地形で、地域ごとの自立性が高かった。糠部を中心とする馬産は中世を通じて全国に知られ、また奈良時代の749年に陸奥国から産出した金が朝廷に献上された記録があり、平泉の中尊寺金色堂もこの産金を背景とする。戦国期には伊達・蘆名・南部・葛西・大崎・相馬・田村などが並立し、統一的な権力は生まれなかった。1589年、伊達政宗が摺上原の戦いで蘆名義広を破って会津を得るが、翌1590年の奥州仕置で会津は没収される。仕置に伴う検地と領主交替は在地に大きな負担をかけ、葛西大崎一揆（1590〜91）や九戸政実の乱（1591）といった抵抗を招いた。慶長3年（1598）の集計では約167万石と全国最大だが、これは広大な面積の合計であり、一人の領主の領国規模を示すものではない。',
    },
    year: [1467, 1600],
    keywords: ['陸奥', '奥州', '伊達氏', '南部氏', '北上川', '奥州仕置'],
    caveat:
      '慶長3年の陸奥の石高約167万石は、出羽の一部（上杉領の置賜郡など）が陸奥側に算入されている可能性が研究者から指摘されており、そのまま国境どおりの生産力とは言えません。また奥羽の検地は地域によって実施の精度に差があり、数値の性格は西国とは異なります。中世の産金量についても同時代の記録が乏しく、確かな数字はわかっていません。',
    gameNote:
      '盤上の石高ランク4は遊びやすさのために調整した相対値です。実際の陸奥は慶長3年の集計で約167万石とされますが、これは一国が突出して強かったというより、面積が極端に広いことによる合計値です。盤上では京から最も遠いマスの一つとして、豊かさと到達しにくさを釣り合わせています。',
  },
};

/**
 * 奥羽山脈と出羽山地に挟まれた国。地形は山（地の利は弓）。
 * 最上川の舟運と日本海の湊が内陸と外をつないだ。
 */
export const DEWA: Province = {
  id: 17,
  kind: 'province',
  name: '出羽',
  reading: 'でわ',
  region: 'tohoku',
  kokudaka: 3,
  defense: 3,
  terrain: 'mountain',
  historicalKokudaka: 31.8,
  note: {
    summary:
      '現在の山形県と秋田県にあたる国。奥羽山脈と出羽山地に挟まれ、最上川が横切って庄内平野へ抜ける。出羽三山の山岳信仰でも知られた。',
    detail: {
      1: 'いまの山形県と秋田県にあたる国です。まわりを山にかこまれ、まん中を最上川が流れて日本海に出ます。羽黒山などの山は、むかしからおまいりの場所でした。山形の最上氏や、秋田の安東氏などがおさめていました。',
      2: '現在の山形県と秋田県にあたる国。東を奥羽山脈、西を出羽山地に区切られ、その間を最上川が横切って庄内平野で日本海に注ぐ。羽黒山・月山・湯殿山の出羽三山は修験道の霊場として多くの参詣者を集めた。戦国期には内陸南部の最上氏、庄内の大宝寺氏、北部の安東（秋田）氏などが並び立ち、庄内をめぐって争いが続いた。最上義光は山形城と城下町を整え、最上川の舟運を整備して物資が動きやすいようにした。1600年には長谷堂城をめぐって上杉方と最上・伊達方が戦っている。',
      3: '現在の山形県と秋田県にあたる国。東縁の奥羽山脈と西の出羽山地に挟まれ、最上川が内陸の盆地群（米沢・山形・新庄）を貫いて庄内平野で日本海に注ぐ。この河川と、土崎湊・酒田といった日本海側の湊が、内陸と畿内・北国とを結ぶ物流路であった。羽黒山・月山・湯殿山の出羽三山は中世を通じて修験道の一大霊場で、東国各地から参詣者と初穂が集まった。戦国期は南に伊達領（置賜）、内陸中部に最上氏、庄内に大宝寺氏、北に安東（秋田）氏、雄勝・平鹿に小野寺氏という多極構造で、1588年の十五里ヶ原の戦いなど庄内の帰属をめぐる抗争が繰り返された。最上義光は山形城の改修と城下町の整備、最上川舟運の開削、庄内平野の開発を進めており、その事績は軍事より在地経営の面が大きい。1600年の慶長出羽合戦では上杉方の直江兼続が長谷堂城を包囲したが落とせず撤退した。慶長3年の石高は約31.8万石とされる。',
    },
    year: [1467, 1600],
    keywords: ['出羽', '最上氏', '安東氏', '最上川', '出羽三山', '庄内平野'],
    caveat:
      '慶長3年の出羽の石高が陸奥に比べて極端に小さいのは、上杉領の置賜郡分などが陸奥側に集計された可能性が指摘されているためで、出羽の生産力がその数値どおりだったとは限りません。また、出羽を代表する産物として知られる最上紅花や、延沢銀山の本格的な稼働は江戸時代に入ってからで、戦国期の規模はよくわかっていません。伊達政宗の母・義姫（最上義光の妹）が最上と伊達の間を取りなしたという逸話は、伊達家が後にまとめた記録に拠る部分が大きく、細部をそのまま史実とは扱えません。',
    gameNote:
      '盤上の石高ランク3は遊びやすさのための相対値です。実際の出羽は慶長3年の集計で約31.8万石とされ、盤上のランクほど陸奥と近い数値ではありません。地形を「山」としているため、合戦では戦場札2枚が弓になります。',
  },
};

/**
 * 日本海に長く面し、青苧の集散と湊の管理が国の富を支えた国。
 * 地形は湊（地の利は鉄砲、第I章では足軽に読み替える）。
 */
export const ECHIGO: Province = {
  id: 19,
  kind: 'province',
  name: '越後',
  reading: 'えちご',
  region: 'hokuriku',
  kokudaka: 3,
  defense: 3,
  terrain: 'coast',
  historicalKokudaka: 39.1,
  yields: [{ res: 'zeni', n: 1 }],
  note: {
    summary:
      '日本海に長く面した国。青苧（あおそ）という繊維の産地で、柏崎・直江津の湊から畿内へ運ばれた。守護代の長尾氏が実権を握り、のちの上杉氏の本拠となった。',
    detail: {
      1: 'いまの新潟県にあたる国です。日本海にそって長くのびていて、船の出入りする港がありました。青苧という、布のもとになる草をたくさんつくり、船で京都のほうへ売っていました。冬は雪がとても深く、いくさに出られない季節がありました。',
      2: '現在の新潟県（佐渡を除く）にあたる国。信濃川と阿賀野川が越後平野をつくるが、戦国時代の平野は低湿地が多く、本格的な新田開発は江戸時代に入ってからだった。この国を支えたのは青苧（からむしの繊維）で、魚沼や頸城でつくられ、柏崎や直江津の湊から若狭の小浜を経て京都・奈良へ運ばれ、上等な布の材料になった。守護代の長尾氏は、この青苧の流通に税をかけ、湊を押さえることで銭を得ていた。長尾景虎（のちの上杉謙信）が国内をまとめ、信濃や関東へたびたび出兵した。',
      3: '現在の新潟県のうち佐渡を除く地域。信濃川・阿賀野川が越後平野を形成するが、戦国期には氾濫原と潟湖が広がる低湿地で、干拓と新田開発の大半は江戸期以降にあたる。国の経済を支えたのは青苧（からむしから採る麻繊維）で、魚沼・頸城郡を中心に生産され、柏崎・直江津から「越後船」で若狭小浜へ、さらに京都・奈良へ運ばれて越後上布などの原料となった。長尾氏の御用商人・蔵田五郎左衛門が座元をつとめる青苧座が成立し、大名権力を背景とした流通課税がおこなわれている。政治的には1507年に守護代長尾為景が守護上杉房能を退けて実権を握り、下剋上の早い例とされる。長尾景虎（上杉謙信）は国内統一後、関東管領上杉氏の名跡を継いで関東へ出兵し、信濃をめぐって武田氏と川中島で争った。謙信の死後は1578年の御館の乱で景勝と景虎が争い、続く新発田重家の乱（1581〜87）で国内は長く荒れた。1598年に上杉景勝が会津へ移され、代わって堀氏が入る。慶長3年の石高は約39.1万石。',
    },
    year: [1467, 1600],
    keywords: ['越後', '上杉氏', '長尾氏', '青苧', '直江津', '御館の乱'],
    caveat:
      '上杉謙信を「義の武将」とする人物像は、江戸時代の軍記物や後世の評価によって形づくられた部分が大きく、同時代の史料からそのまま導けるものではありません。近年の研究では、関東への度重なる出兵について、越後の兵や国人への恩賞・略奪（乱取り）といった経済的な側面からも検討が進んでいます。合戦の回数や勝敗の集計として語られる数字も、典拠のはっきりしないものが多くあります。',
    gameNote:
      '盤上の石高ランク3は遊びやすさのための相対値です。実際の越後は慶長3年の集計で約39.1万石とされます。銭の産出（yields）を設定していますが、これは金山ではなく、青苧の流通課税と直江津・柏崎の湊からの収入を表したものです。佐渡金山は越後国ではなく佐渡国にあり、盤上には国マスとして置いていません。地形を「湊」としているため、合戦では戦場札2枚が鉄砲（第I章では足軽に読み替え）になります。',
  },
};
